import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, CalendarPlus, Loader2, Megaphone, Send, Sparkles, Wrench } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import type { AiCampaignSuggestions, AiChatResponse, AiDailySummary, AiProposedAction, AiStatus } from '@barbeiropro/contracts';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { useSession } from '@/lib/session';

const QUICK = ['Quais horários estão vagos amanhã?', 'Clientes que não voltam há 60 dias', 'Faturamento deste mês'];

const TOOL_LABELS: Record<string, string> = {
  agendaSummary: 'Resumo da agenda',
  findIdleSlots: 'Horários livres',
  inactiveCustomers: 'Clientes inativos',
  searchCustomers: 'Busca de clientes',
  queryKpi: 'Indicadores',
  listServices: 'Serviços',
  businessHours: 'Horário de funcionamento',
  proposeAppointment: 'Proposta de agendamento',
  proposeCampaign: 'Proposta de campanha',
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: string[];
  actions?: AiProposedAction[];
}

/** Leva à tela real, pré-preenchida. A IA nunca executa: o usuário confirma lá. */
export function actionHref(a: AiProposedAction): string {
  const p = a.payload as Record<string, unknown>;
  const q = new URLSearchParams();
  if (a.kind === 'create_appointment') {
    q.set('novo', '1');
    for (const k of ['branchId', 'barberId', 'serviceId', 'customerId', 'startsAt', 'notes']) if (p[k]) q.set(k, String(p[k]));
    return `/app/agenda?${q}`;
  }
  q.set('nova', '1');
  for (const k of ['name', 'inactiveDays', 'message']) if (p[k] !== undefined && p[k] !== null) q.set(k, String(p[k]));
  return `/app/campanhas?${q}`;
}

export function AssistantPage() {
  const status = useQuery({ queryKey: ['ai', 'status'], queryFn: () => api.get<AiStatus>('/ai/status') });

  return (
    <div>
      <PageHeader title="Assistente" description="Pergunte sobre agenda, clientes e faturamento. Ele consulta os dados reais e propõe ações; você confirma nas telas." />
      {status.isLoading ? (
        <LoadingState />
      ) : status.error ? (
        <ErrorState error={status.error} onRetry={() => status.refetch()} />
      ) : !status.data?.available ? (
        <EmptyState
          title="Assistente indisponível"
          description={status.data?.message ?? 'O assistente de IA não está disponível no momento. Fale com o suporte.'}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Chat />
          <div className="grid content-start gap-6">
            <DailySummary />
            <CampaignSuggestions />
          </div>
        </div>
      )}
    </div>
  );
}

function Chat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (history: ChatMessage[]) =>
      api.post<AiChatResponse>('/ai/chat', { messages: history.slice(-20).map(({ role, content }) => ({ role, content })) }),
    onSuccess: (r) =>
      setMessages((m) => [...m, { role: 'assistant', content: r.reply, toolsUsed: r.toolsUsed, actions: r.proposedActions }]),
    onError: (e) => toast.error(errorMessage(e)),
  });

  useEffect(() => endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' }), [messages, send.isPending]);

  const ask = (content: string) => {
    const value = content.trim();
    if (!value || send.isPending) return;
    const next = [...messages, { role: 'user' as const, content: value }];
    setMessages(next);
    setText('');
    send.mutate(next);
  };

  return (
    <section aria-label="Conversa com o assistente" className="flex min-h-[28rem] flex-col rounded-lg border bg-card">
      <div className="flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
        {messages.length === 0 && (
          <div className="grid gap-3 py-6 text-center">
            <Bot className="mx-auto size-6 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">Comece por uma sugestão ou escreva sua pergunta.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {QUICK.map((q) => (
                <Button key={q} variant="outline" size="sm" onClick={() => ask(q)}>
                  {q}
                </Button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} m={m} />
        ))}
        {send.isPending && (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Consultando os dados…
          </p>
        )}
        <div ref={endRef} />
      </div>
      <form
        className="flex items-end gap-2 border-t p-3"
        onSubmit={(e) => {
          e.preventDefault();
          ask(text);
        }}
      >
        <label htmlFor="ai-input" className="sr-only">
          Pergunta para o assistente
        </label>
        <Textarea
          id="ai-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              ask(text);
            }
          }}
          maxLength={4000}
          rows={2}
          placeholder="Pergunte sobre agenda, clientes ou faturamento"
          className="min-h-0 resize-none"
        />
        <Button type="submit" disabled={send.isPending || !text.trim()}>
          <Send className="size-4" aria-hidden /> Enviar pergunta
        </Button>
      </form>
    </section>
  );
}

function MessageBubble({ m }: { m: ChatMessage }) {
  const mine = m.role === 'user';
  return (
    <div className={mine ? 'flex justify-end' : 'flex justify-start'}>
      <div className={`max-w-[85%] space-y-2 rounded-lg px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
        <p className="whitespace-pre-wrap">{m.content}</p>
        {!!m.toolsUsed?.length && (
          <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <Wrench className="size-3" aria-hidden /> Consultou: {m.toolsUsed.map((t) => TOOL_LABELS[t] ?? t).join(', ')}
          </p>
        )}
        {m.actions?.map((a, i) => (
          <Button key={i} asChild variant="outline" size="sm" className="h-auto w-full justify-start whitespace-normal bg-background py-2 text-left text-foreground">
            <Link to={actionHref(a)}>
              {a.kind === 'create_appointment' ? <CalendarPlus className="size-4 shrink-0" aria-hidden /> : <Megaphone className="size-4 shrink-0" aria-hidden />}
              <span>
                {a.label}
                <span className="block text-xs font-normal text-muted-foreground">Abre a tela para você revisar e confirmar</span>
              </span>
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
}

function DailySummary() {
  const summary = useMutation({
    mutationFn: () => api.get<AiDailySummary>('/ai/daily-summary'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium">Resumo de hoje</h2>
        <Button size="sm" variant="outline" onClick={() => summary.mutate()} disabled={summary.isPending}>
          {summary.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Sparkles className="size-4" aria-hidden />}
          {summary.data ? 'Gerar de novo' : 'Gerar resumo'}
        </Button>
      </div>
      {summary.isPending && <p role="status" className="mt-3 text-sm text-muted-foreground">Lendo a agenda…</p>}
      {summary.error && <div className="mt-3"><ErrorState error={summary.error} onRetry={() => summary.mutate()} /></div>}
      {summary.data && <p className="mt-3 whitespace-pre-wrap text-sm">{summary.data.summary}</p>}
      {!summary.data && !summary.isPending && !summary.error && (
        <p className="mt-3 text-sm text-muted-foreground">Gere sob demanda um resumo da agenda de hoje.</p>
      )}
    </section>
  );
}

function CampaignSuggestions() {
  const { can } = useSession();
  const suggestions = useMutation({
    mutationFn: () => api.get<AiCampaignSuggestions>('/ai/campaign-suggestions'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!can('campaigns.manage')) return null;
  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium">Campanhas de retorno</h2>
        <Button size="sm" variant="outline" onClick={() => suggestions.mutate()} disabled={suggestions.isPending}>
          {suggestions.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Megaphone className="size-4" aria-hidden />}
          Sugerir campanhas
        </Button>
      </div>
      {suggestions.isPending && <p role="status" className="mt-3 text-sm text-muted-foreground">Analisando clientes inativos…</p>}
      {suggestions.error && <div className="mt-3"><ErrorState error={suggestions.error} onRetry={() => suggestions.mutate()} /></div>}
      {suggestions.data && suggestions.data.suggestions.length === 0 && <p className="mt-3 text-sm text-muted-foreground">Nenhuma sugestão no momento.</p>}
      <ul className="mt-3 grid gap-3">
        {suggestions.data?.suggestions.map((s, i) => (
          <li key={i} className="rounded-md border p-3 text-sm">
            <p className="font-medium">{s.title}</p>
            <p className="mt-1 text-muted-foreground">{s.rationale}</p>
            <p className="mt-2 tabular text-xs">
              Sem visita há {s.inactiveDays}+ dias: {s.audienceSize} cliente(s), {s.consentedSize} com consentimento de marketing
            </p>
            <blockquote className="mt-2 border-l-2 pl-3 text-muted-foreground">{s.message}</blockquote>
            <Button asChild size="sm" variant="outline" className="mt-3">
              <Link to={actionHref({ kind: 'create_campaign', label: s.title, payload: { name: s.title, inactiveDays: s.inactiveDays, message: s.message } })}>
                Revisar em campanhas
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
